import {
  Exercise,
  PerformedSet,
  TrainingRoutineSession,
  PerformedSetGroup,
  SessionStatistic,
  UserStatistics,
} from '../types';
import { groupItemsByWeek } from './timerUtils';
import { getAllSets, isCompleteSet, isNumeric } from './utils';

export const getWeeklyCountWorkouts = (
  trainingRoutineSessions: TrainingRoutineSession[],
): number => {
  const currentDate = new Date();
  const currentDayOfWeek = currentDate.getDay(); // 0 is Sunday, 1 is Monday, ..., 6 is Saturday

  // Adjust current day to Monday if today is not Sunday
  const daysToMonday = currentDayOfWeek === 0 ? 6 : currentDayOfWeek - 1;
  currentDate.setDate(currentDate.getDate() - daysToMonday);
  currentDate.setHours(0, 0, 0, 0);

  const lastMonday = currentDate.getTime();

  return trainingRoutineSessions.filter(
    ({ createdDate }) => new Date(createdDate).getTime() > lastMonday,
  ).length;
};

export const getMaxWorkoutsPerWeek = (
  trainingRoutineSessions: TrainingRoutineSession[],
): number => {
  const groupedWorkoutsPerWeek = groupItemsByWeek(trainingRoutineSessions);
  return Math.max(...groupedWorkoutsPerWeek.map((week) => week.length));
};

export const getAverageSetsCompletion = (
  trainingRoutineSessions: TrainingRoutineSession[],
): number => {
  return Math.round(
    trainingRoutineSessions.reduce(
      (acc, session) => acc + getTrainingSessionSetsCompletion(session),
      0,
    ) / trainingRoutineSessions.length,
  );
};

const getPerformedSetVolume = (
  exercises: Record<string, Exercise>,
  { exerciseId, reps, performedReps, weight, performedWeight }: PerformedSet,
  usePerformedVolume: boolean,
): number => {
  const unilateralMultiplyer = exercises[exerciseId]?.isUnilateral ? 2 : 1;
  const weightsMultiplyer = exercises[exerciseId]?.isWeightsPerSide ? 2 : 1;
  if (usePerformedVolume) {
    if (performedReps && performedWeight) {
      const singleVolume = performedReps * performedWeight;
      return singleVolume * unilateralMultiplyer * weightsMultiplyer;
    }
  } else if (reps && weight) {
    const singleVolume = reps * weight;
    return singleVolume * unilateralMultiplyer * weightsMultiplyer;
  }
  return 0;
};

const calculatePerformedSetsVolume = (
  exercises: Record<string, Exercise>,
  performedSets: PerformedSetGroup,
  usePerformedVolume: boolean,
): number => {
  return performedSets.reduce(
    (acc, set) =>
      acc + getPerformedSetVolume(exercises, set, usePerformedVolume),
    0,
  );
};

export const getTrainingSessionPerformedVolume = (
  exercises: Record<string, Exercise>,
  { performedRoutine }: TrainingRoutineSession,
): number => {
  const completedSets = getAllSets(
    performedRoutine.performedExerciseGroups,
  ).filter(isCompleteSet);
  return calculatePerformedSetsVolume(exercises, completedSets, true);
};

export const getTrainingSessionTotalVolume = (
  exercises: Record<string, Exercise>,
  { performedRoutine }: TrainingRoutineSession,
): number => {
  const allSets = getAllSets(performedRoutine.performedExerciseGroups);
  return calculatePerformedSetsVolume(exercises, allSets, false);
};

export const getTrainingSessionVolumeCompletion = (
  exercises: Record<string, Exercise>,
  trainingRoutineSession: TrainingRoutineSession,
): number => {
  return Math.round(
    (getTrainingSessionPerformedVolume(exercises, trainingRoutineSession) /
      getTrainingSessionTotalVolume(exercises, trainingRoutineSession)) *
      100,
  );
};

export const getAverageVolumeCompletion = (
  exercises: Record<string, Exercise>,
  trainingRoutineSession: TrainingRoutineSession[],
): number => {
  return Math.round(
    trainingRoutineSession.reduce(
      (acc, session) =>
        acc + getTrainingSessionVolumeCompletion(exercises, session),
      0,
    ) / trainingRoutineSession.length,
  );
};

export const getTrainingSessionSetsCompletion = ({
  performedRoutine,
}: TrainingRoutineSession): number => {
  const allSets = getAllSets(performedRoutine.performedExerciseGroups);
  const completedSets = allSets.filter(isCompleteSet);
  return Math.ceil((completedSets.length / allSets.length) * 100);
};

export const adjustPercentageToText = (percentage: number): string => {
  if (percentage > 0) {
    return `${percentage}`;
  }
  return '0';
};

export const getWorkoutStreaks = (
  trainingRoutineSession: TrainingRoutineSession[],
): { lastStreak: number; highestStreak: number } => {
  if (trainingRoutineSession.length === 0) {
    return {
      lastStreak: 0,
      highestStreak: 0,
    };
  }

  const trainingsGroupedByWeeks = groupItemsByWeek(trainingRoutineSession);
  let lastStreak = 0;
  let currentStreak = 0;
  let highestStreak = 0;

  trainingsGroupedByWeeks.forEach((trainingsInWeek) => {
    if (trainingsInWeek.length > 0) {
      // Count the current streak
      currentStreak++;
      // Update the highest streak if needed
      highestStreak = Math.max(highestStreak, currentStreak);
    } else {
      // Reset the current streak if the array is empty
      currentStreak = 0;
    }

    // Update the last streak for each row
    lastStreak = trainingsInWeek.length > 0 ? currentStreak : 0;
  });

  return { lastStreak, highestStreak };
};

export const getExerciseWeightsHistory = (
  exercise: Exercise,
  trainingRoutineSessions: TrainingRoutineSession[],
): number[] => {
  return trainingRoutineSessions
    .reduce((exerciseWeightsHistory, { performedRoutine }) => {
      const completedSets = getAllSets(
        performedRoutine.performedExerciseGroups,
      ).filter(isCompleteSet);
      completedSets.forEach(({ exerciseId, performedWeight }) => {
        if (exerciseId === exercise.id && isNumeric(performedWeight)) {
          exerciseWeightsHistory.push(performedWeight);
        }
      });
      return exerciseWeightsHistory;
    }, [] as number[])
    .slice(-10);
};

// How much exercises passed the history performedDuration
export const getPersonalRecords = (
  currentSession: TrainingRoutineSession,
  historySessions: TrainingRoutineSession[],
): number => {
  let personalRecords = 0;
  const currentMaxWeights = new Map<string, number>();
  currentSession.performedRoutine.performedExerciseGroups.forEach((group) => {
    group.sets.forEach((set) => {
      set.forEach((performedSet) => {
        if (performedSet.performedWeight !== null) {
          const currentMax =
            currentMaxWeights.get(performedSet.exerciseId) || 0;
          if (performedSet.performedWeight > currentMax) {
            currentMaxWeights.set(
              performedSet.exerciseId,
              performedSet.performedWeight,
            );
          }
        }
      });
    });
  });
  currentMaxWeights.forEach((weight, exerciseId) => {
    let isPersonalRecord = true;
    historySessions.forEach((session) => {
      session.performedRoutine.performedExerciseGroups.forEach((group) => {
        group.sets.forEach((set) => {
          set.forEach((performedSet) => {
            if (
              performedSet.exerciseId === exerciseId &&
              performedSet.performedWeight !== null
            ) {
              if (performedSet.performedWeight >= weight) {
                isPersonalRecord = false;
              }
            }
          });
        });
      });
    });
    if (isPersonalRecord) {
      personalRecords++;
    }
  });
  return personalRecords;
};

const sumSessionMetric = (
  sessions: SessionStatistic[] | null | undefined,
  selector: (session: SessionStatistic) => number,
): number => {
  if (!sessions?.length) {
    return 0;
  }
  return sessions.reduce((acc, session) => acc + selector(session), 0);
};

const toPercentage = (numerator: number, denominator: number): number => {
  if (!denominator) {
    return 0;
  }
  return Math.round((numerator / denominator) * 100);
};

export const calculateUserStatistics = (
  lastWorkoutSessionStatistics: SessionStatistic | null,
  last7DaysSessionStatistics: SessionStatistic[],
  last30DaysSessionStatistics: SessionStatistic[],
  allTimeSessionStatistics: SessionStatistic[],
): UserStatistics => {
  if (
    !lastWorkoutSessionStatistics ||
    !last7DaysSessionStatistics ||
    !last30DaysSessionStatistics ||
    !allTimeSessionStatistics
  ) {
    return {
      lastWorkoutRepsCompletionPercentage: 0,
      lastWeekRepsCompletionPercentage: 0,
      lastMonthRepsCompletionPercentage: 0,
      allTimeRepsCompletionPercentage: 0,
      lastWorkoutConcentricGoodRepsPercentage: 0,
      lastWorkoutConcentricSlowRepsPercentage: 0,
      lastWorkoutConcentricFastRepsPercentage: 0,
      lastWorkoutConcentricMissedRepsPercentage: 0,
      lastWeekConcentricGoodRepsPercentage: 0,
      lastWeekConcentricSlowRepsPercentage: 0,
      lastWeekConcentricFastRepsPercentage: 0,
      lastWeekConcentricMissedRepsPercentage: 0,
      lastMonthConcentricGoodRepsPercentage: 0,
      lastMonthConcentricSlowRepsPercentage: 0,
      lastMonthConcentricFastRepsPercentage: 0,
      lastMonthConcentricMissedRepsPercentage: 0,
      allTimeConcentricGoodRepsPercentage: 0,
      allTimeConcentricSlowRepsPercentage: 0,
      allTimeConcentricFastRepsPercentage: 0,
      allTimeConcentricMissedRepsPercentage: 0,
      lastWorkoutEccentricGoodRepsPercentage: 0,
      lastWorkoutEccentricSlowRepsPercentage: 0,
      lastWorkoutEccentricFastRepsPercentage: 0,
      lastWorkoutEccentricMissedRepsPercentage: 0,
      lastWeekEccentricGoodRepsPercentage: 0,
      lastWeekEccentricSlowRepsPercentage: 0,
      lastWeekEccentricFastRepsPercentage: 0,
      lastWeekEccentricMissedRepsPercentage: 0,
      lastMonthEccentricGoodRepsPercentage: 0,
      lastMonthEccentricSlowRepsPercentage: 0,
      lastMonthEccentricFastRepsPercentage: 0,
      lastMonthEccentricMissedRepsPercentage: 0,
      allTimeEccentricGoodRepsPercentage: 0,
      allTimeEccentricSlowRepsPercentage: 0,
      allTimeEccentricFastRepsPercentage: 0,
      allTimeEccentricMissedRepsPercentage: 0,
    };
  }
  const lastWorkoutSessionStatistic = lastWorkoutSessionStatistics;
  const lastWeekSessionStatistics = last7DaysSessionStatistics;
  const lastMonthSessionStatistics = last30DaysSessionStatistics;
  const lastWeekTotalReps = sumSessionMetric(
    lastWeekSessionStatistics,
    (session) => session.totalReps,
  );
  const lastWeekTotalPerformedReps = sumSessionMetric(
    lastWeekSessionStatistics,
    (session) => session.totalPerformedReps,
  );
  const lastWeekAiTotalReps = sumSessionMetric(
    lastWeekSessionStatistics,
    (session) => session.aiTotalReps,
  );
  const lastWeekAiTotalPerformedReps = sumSessionMetric(
    lastWeekSessionStatistics,
    (session) => session.aiTotalPerformedReps,
  );

  const lastMonthTotalReps = sumSessionMetric(
    lastMonthSessionStatistics,
    (session) => session.totalReps,
  );
  const lastMonthTotalPerformedReps = sumSessionMetric(
    lastMonthSessionStatistics,
    (session) => session.totalPerformedReps,
  );
  const lastMonthAiTotalReps = sumSessionMetric(
    lastMonthSessionStatistics,
    (session) => session.aiTotalReps,
  );
  const lastMonthAiTotalPerformedReps = sumSessionMetric(
    lastMonthSessionStatistics,
    (session) => session.aiTotalPerformedReps,
  );

  const allTimeTotalReps = sumSessionMetric(
    allTimeSessionStatistics,
    (session) => session.totalReps,
  );
  const allTimeTotalPerformedReps = sumSessionMetric(
    allTimeSessionStatistics,
    (session) => session.totalPerformedReps,
  );
  const allTimeAiTotalReps = sumSessionMetric(
    allTimeSessionStatistics,
    (session) => session.aiTotalReps,
  );
  const allTimeAiTotalPerformedReps = sumSessionMetric(
    allTimeSessionStatistics,
    (session) => session.aiTotalPerformedReps,
  );

  const lastWorkoutRepsCompletionPercentage = toPercentage(
    lastWorkoutSessionStatistic.totalPerformedReps,
    lastWorkoutSessionStatistic.totalReps,
  );
  const lastWeekRepsCompletionPercentage = toPercentage(
    lastWeekTotalPerformedReps,
    lastWeekTotalReps,
  );
  const lastMonthRepsCompletionPercentage = toPercentage(
    lastMonthTotalPerformedReps,
    lastMonthTotalReps,
  );
  const allTimeRepsCompletionPercentage = toPercentage(
    allTimeTotalPerformedReps,
    allTimeTotalReps,
  );

  const lastWorkoutAiTotalReps = lastWorkoutSessionStatistic.aiTotalReps;
  const lastWorkoutAiTotalPerformedReps =
    lastWorkoutSessionStatistic.aiTotalPerformedReps;

  const lastWorkoutConcentricGoodRepsPercentage = toPercentage(
    lastWorkoutSessionStatistic.goodConcentricReps,
    lastWorkoutAiTotalReps,
  );
  const lastWorkoutConcentricSlowRepsPercentage = toPercentage(
    lastWorkoutSessionStatistic.slowConcentricReps,
    lastWorkoutAiTotalReps,
  );
  const lastWorkoutConcentricFastRepsPercentage = toPercentage(
    lastWorkoutSessionStatistic.fastConcentricReps,
    lastWorkoutAiTotalReps,
  );
  const lastWorkoutConcentricMissedRepsPercentage = toPercentage(
    lastWorkoutAiTotalReps - lastWorkoutAiTotalPerformedReps,
    lastWorkoutAiTotalReps,
  );

  const lastWorkoutEccentricGoodRepsPercentage = toPercentage(
    lastWorkoutSessionStatistic.goodEccentricReps,
    lastWorkoutAiTotalReps,
  );
  const lastWorkoutEccentricSlowRepsPercentage = toPercentage(
    lastWorkoutSessionStatistic.slowEccentricReps,
    lastWorkoutAiTotalReps,
  );
  const lastWorkoutEccentricFastRepsPercentage = toPercentage(
    lastWorkoutSessionStatistic.fastEccentricReps,
    lastWorkoutAiTotalReps,
  );
  const lastWorkoutEccentricMissedRepsPercentage = toPercentage(
    lastWorkoutAiTotalReps - lastWorkoutAiTotalPerformedReps,
    lastWorkoutAiTotalReps,
  );

  const lastWeekConcentricGoodRepsPercentage = toPercentage(
    sumSessionMetric(
      lastWeekSessionStatistics,
      (session) => session.goodConcentricReps,
    ),
    lastWeekAiTotalReps,
  );
  const lastWeekConcentricSlowRepsPercentage = toPercentage(
    sumSessionMetric(
      lastWeekSessionStatistics,
      (session) => session.slowConcentricReps,
    ),
    lastWeekAiTotalReps,
  );
  const lastWeekConcentricFastRepsPercentage = toPercentage(
    sumSessionMetric(
      lastWeekSessionStatistics,
      (session) => session.fastConcentricReps,
    ),
    lastWeekAiTotalReps,
  );
  const lastWeekConcentricMissedRepsPercentage = toPercentage(
    lastWeekAiTotalReps - lastWeekAiTotalPerformedReps,
    lastWeekAiTotalReps,
  );

  const lastWeekEccentricGoodRepsPercentage = toPercentage(
    sumSessionMetric(
      lastWeekSessionStatistics,
      (session) => session.goodEccentricReps,
    ),
    lastWeekAiTotalReps,
  );
  const lastWeekEccentricSlowRepsPercentage = toPercentage(
    sumSessionMetric(
      lastWeekSessionStatistics,
      (session) => session.slowEccentricReps,
    ),
    lastWeekAiTotalReps,
  );
  const lastWeekEccentricFastRepsPercentage = toPercentage(
    sumSessionMetric(
      lastWeekSessionStatistics,
      (session) => session.fastEccentricReps,
    ),
    lastWeekAiTotalReps,
  );
  const lastWeekEccentricMissedRepsPercentage = toPercentage(
    lastWeekAiTotalReps - lastWeekAiTotalPerformedReps,
    lastWeekAiTotalReps,
  );

  const lastMonthConcentricGoodRepsPercentage = toPercentage(
    sumSessionMetric(
      lastMonthSessionStatistics,
      (session) => session.goodConcentricReps,
    ),
    lastMonthAiTotalReps,
  );
  const lastMonthConcentricSlowRepsPercentage = toPercentage(
    sumSessionMetric(
      lastMonthSessionStatistics,
      (session) => session.slowConcentricReps,
    ),
    lastMonthAiTotalReps,
  );
  const lastMonthConcentricFastRepsPercentage = toPercentage(
    sumSessionMetric(
      lastMonthSessionStatistics,
      (session) => session.fastConcentricReps,
    ),
    lastMonthAiTotalReps,
  );
  const lastMonthConcentricMissedRepsPercentage = toPercentage(
    lastMonthAiTotalReps - lastMonthAiTotalPerformedReps,
    lastMonthAiTotalReps,
  );

  const lastMonthEccentricGoodRepsPercentage = toPercentage(
    sumSessionMetric(
      lastMonthSessionStatistics,
      (session) => session.goodEccentricReps,
    ),
    lastMonthAiTotalReps,
  );
  const lastMonthEccentricSlowRepsPercentage = toPercentage(
    sumSessionMetric(
      lastMonthSessionStatistics,
      (session) => session.slowEccentricReps,
    ),
    lastMonthAiTotalReps,
  );
  const lastMonthEccentricFastRepsPercentage = toPercentage(
    sumSessionMetric(
      lastMonthSessionStatistics,
      (session) => session.fastEccentricReps,
    ),
    lastMonthAiTotalReps,
  );
  const lastMonthEccentricMissedRepsPercentage = toPercentage(
    lastMonthAiTotalReps - lastMonthAiTotalPerformedReps,
    lastMonthAiTotalReps,
  );

  const allTimeConcentricGoodRepsPercentage = toPercentage(
    sumSessionMetric(
      allTimeSessionStatistics,
      (session) => session.goodConcentricReps,
    ),
    allTimeAiTotalReps,
  );
  const allTimeConcentricSlowRepsPercentage = toPercentage(
    sumSessionMetric(
      allTimeSessionStatistics,
      (session) => session.slowConcentricReps,
    ),
    allTimeAiTotalReps,
  );
  const allTimeConcentricFastRepsPercentage = toPercentage(
    sumSessionMetric(
      allTimeSessionStatistics,
      (session) => session.fastConcentricReps,
    ),
    allTimeAiTotalReps,
  );
  const allTimeConcentricMissedRepsPercentage = toPercentage(
    allTimeAiTotalReps - allTimeAiTotalPerformedReps,
    allTimeAiTotalReps,
  );

  const allTimeEccentricGoodRepsPercentage = toPercentage(
    sumSessionMetric(
      allTimeSessionStatistics,
      (session) => session.goodEccentricReps,
    ),
    allTimeAiTotalReps,
  );
  const allTimeEccentricSlowRepsPercentage = toPercentage(
    sumSessionMetric(
      allTimeSessionStatistics,
      (session) => session.slowEccentricReps,
    ),
    allTimeAiTotalReps,
  );
  const allTimeEccentricFastRepsPercentage = toPercentage(
    sumSessionMetric(
      allTimeSessionStatistics,
      (session) => session.fastEccentricReps,
    ),
    allTimeAiTotalReps,
  );
  const allTimeEccentricMissedRepsPercentage = toPercentage(
    allTimeAiTotalReps - allTimeAiTotalPerformedReps,
    allTimeAiTotalReps,
  );

  return {
    lastWorkoutRepsCompletionPercentage,
    lastWeekRepsCompletionPercentage,
    lastMonthRepsCompletionPercentage,
    allTimeRepsCompletionPercentage,
    lastWorkoutConcentricGoodRepsPercentage,
    lastWorkoutConcentricSlowRepsPercentage,
    lastWorkoutConcentricFastRepsPercentage,
    lastWorkoutConcentricMissedRepsPercentage,
    lastWeekConcentricGoodRepsPercentage,
    lastWeekConcentricSlowRepsPercentage,
    lastWeekConcentricFastRepsPercentage,
    lastWeekConcentricMissedRepsPercentage,
    lastMonthConcentricGoodRepsPercentage,
    lastMonthConcentricSlowRepsPercentage,
    lastMonthConcentricFastRepsPercentage,
    lastMonthConcentricMissedRepsPercentage,
    allTimeConcentricGoodRepsPercentage,
    allTimeConcentricSlowRepsPercentage,
    allTimeConcentricFastRepsPercentage,
    allTimeConcentricMissedRepsPercentage,
    lastWorkoutEccentricGoodRepsPercentage,
    lastWorkoutEccentricSlowRepsPercentage,
    lastWorkoutEccentricFastRepsPercentage,
    lastWorkoutEccentricMissedRepsPercentage,
    lastWeekEccentricGoodRepsPercentage,
    lastWeekEccentricSlowRepsPercentage,
    lastWeekEccentricFastRepsPercentage,
    lastWeekEccentricMissedRepsPercentage,
    lastMonthEccentricGoodRepsPercentage,
    lastMonthEccentricSlowRepsPercentage,
    lastMonthEccentricFastRepsPercentage,
    lastMonthEccentricMissedRepsPercentage,
    allTimeEccentricGoodRepsPercentage,
    allTimeEccentricSlowRepsPercentage,
    allTimeEccentricFastRepsPercentage,
    allTimeEccentricMissedRepsPercentage,
  };
};

export const calculateOverallStatistics = (
  lastWorkoutSessionStatistics: SessionStatistic[],
  last7DaysSessionStatistics: SessionStatistic[],
  last30DaysSessionStatistics: SessionStatistic[],
  allTimeSessionStatistics: SessionStatistic[],
): UserStatistics => {
  if (
    !lastWorkoutSessionStatistics ||
    !last7DaysSessionStatistics ||
    !last30DaysSessionStatistics ||
    !allTimeSessionStatistics
  ) {
    return {
      lastWorkoutRepsCompletionPercentage: 0,
      lastWeekRepsCompletionPercentage: 0,
      lastMonthRepsCompletionPercentage: 0,
      allTimeRepsCompletionPercentage: 0,
      lastWorkoutConcentricGoodRepsPercentage: 0,
      lastWorkoutConcentricSlowRepsPercentage: 0,
      lastWorkoutConcentricFastRepsPercentage: 0,
      lastWorkoutConcentricMissedRepsPercentage: 0,
      lastWeekConcentricGoodRepsPercentage: 0,
      lastWeekConcentricSlowRepsPercentage: 0,
      lastWeekConcentricFastRepsPercentage: 0,
      lastWeekConcentricMissedRepsPercentage: 0,
      lastMonthConcentricGoodRepsPercentage: 0,
      lastMonthConcentricSlowRepsPercentage: 0,
      lastMonthConcentricFastRepsPercentage: 0,
      lastMonthConcentricMissedRepsPercentage: 0,
      allTimeConcentricGoodRepsPercentage: 0,
      allTimeConcentricSlowRepsPercentage: 0,
      allTimeConcentricFastRepsPercentage: 0,
      allTimeConcentricMissedRepsPercentage: 0,
      lastWorkoutEccentricGoodRepsPercentage: 0,
      lastWorkoutEccentricSlowRepsPercentage: 0,
      lastWorkoutEccentricFastRepsPercentage: 0,
      lastWorkoutEccentricMissedRepsPercentage: 0,
      lastWeekEccentricGoodRepsPercentage: 0,
      lastWeekEccentricSlowRepsPercentage: 0,
      lastWeekEccentricFastRepsPercentage: 0,
      lastWeekEccentricMissedRepsPercentage: 0,
      lastMonthEccentricGoodRepsPercentage: 0,
      lastMonthEccentricSlowRepsPercentage: 0,
      lastMonthEccentricFastRepsPercentage: 0,
      lastMonthEccentricMissedRepsPercentage: 0,
      allTimeEccentricGoodRepsPercentage: 0,
      allTimeEccentricSlowRepsPercentage: 0,
      allTimeEccentricFastRepsPercentage: 0,
      allTimeEccentricMissedRepsPercentage: 0,
    };
  }
  const lastWeekSessionStatistics = last7DaysSessionStatistics;
  const lastMonthSessionStatistics = last30DaysSessionStatistics;

  const lastWorkoutTotalReps = sumSessionMetric(
    lastWorkoutSessionStatistics,
    (session) => session.totalReps,
  );
  const lastWorkoutTotalPerformedReps = sumSessionMetric(
    lastWorkoutSessionStatistics,
    (session) => session.totalPerformedReps,
  );
  const lastWorkoutAiTotalReps = sumSessionMetric(
    lastWorkoutSessionStatistics,
    (session) => session.aiTotalReps,
  );
  const lastWorkoutAiTotalPerformedReps = sumSessionMetric(
    lastWorkoutSessionStatistics,
    (session) => session.aiTotalPerformedReps,
  );
  const lastWeekTotalReps = sumSessionMetric(
    lastWeekSessionStatistics,
    (session) => session.totalReps,
  );
  const lastWeekTotalPerformedReps = sumSessionMetric(
    lastWeekSessionStatistics,
    (session) => session.totalPerformedReps,
  );
  const lastWeekAiTotalReps = sumSessionMetric(
    lastWeekSessionStatistics,
    (session) => session.aiTotalReps,
  );
  const lastWeekAiTotalPerformedReps = sumSessionMetric(
    lastWeekSessionStatistics,
    (session) => session.aiTotalPerformedReps,
  );
  const lastMonthTotalReps = sumSessionMetric(
    lastMonthSessionStatistics,
    (session) => session.totalReps,
  );
  const lastMonthTotalPerformedReps = sumSessionMetric(
    lastMonthSessionStatistics,
    (session) => session.totalPerformedReps,
  );
  const lastMonthAiTotalReps = sumSessionMetric(
    lastMonthSessionStatistics,
    (session) => session.aiTotalReps,
  );
  const lastMonthAiTotalPerformedReps = sumSessionMetric(
    lastMonthSessionStatistics,
    (session) => session.aiTotalPerformedReps,
  );
  const allTimeTotalReps = sumSessionMetric(
    allTimeSessionStatistics,
    (session) => session.totalReps,
  );
  const allTimeTotalPerformedReps = sumSessionMetric(
    allTimeSessionStatistics,
    (session) => session.totalPerformedReps,
  );
  const allTimeAiTotalReps = sumSessionMetric(
    allTimeSessionStatistics,
    (session) => session.aiTotalReps,
  );
  const allTimeAiTotalPerformedReps = sumSessionMetric(
    allTimeSessionStatistics,
    (session) => session.aiTotalPerformedReps,
  );

  const lastWorkoutRepsCompletionPercentage = toPercentage(
    lastWorkoutTotalPerformedReps,
    lastWorkoutTotalReps,
  );
  const lastWeekRepsCompletionPercentage = toPercentage(
    lastWeekTotalPerformedReps,
    lastWeekTotalReps,
  );
  const lastMonthRepsCompletionPercentage = toPercentage(
    lastMonthTotalPerformedReps,
    lastMonthTotalReps,
  );
  const allTimeRepsCompletionPercentage = toPercentage(
    allTimeTotalPerformedReps,
    allTimeTotalReps,
  );
  const lastWorkoutConcentricGoodRepsPercentage = toPercentage(
    sumSessionMetric(
      lastWorkoutSessionStatistics,
      (session) => session.goodConcentricReps,
    ),
    lastWorkoutAiTotalReps,
  );
  const lastWorkoutConcentricSlowRepsPercentage = toPercentage(
    sumSessionMetric(
      lastWorkoutSessionStatistics,
      (session) => session.slowConcentricReps,
    ),
    lastWorkoutAiTotalReps,
  );
  const lastWorkoutConcentricFastRepsPercentage = toPercentage(
    sumSessionMetric(
      lastWorkoutSessionStatistics,
      (session) => session.fastConcentricReps,
    ),
    lastWorkoutAiTotalReps,
  );
  const lastWorkoutEccentricGoodRepsPercentage = toPercentage(
    sumSessionMetric(
      lastWorkoutSessionStatistics,
      (session) => session.goodEccentricReps,
    ),
    lastWorkoutAiTotalReps,
  );
  const lastWorkoutEccentricSlowRepsPercentage = toPercentage(
    sumSessionMetric(
      lastWorkoutSessionStatistics,
      (session) => session.slowEccentricReps,
    ),
    lastWorkoutAiTotalReps,
  );
  const lastWorkoutEccentricFastRepsPercentage = toPercentage(
    sumSessionMetric(
      lastWorkoutSessionStatistics,
      (session) => session.fastEccentricReps,
    ),
    lastWorkoutAiTotalReps,
  );
  const lastWeekConcentricGoodRepsPercentage = toPercentage(
    sumSessionMetric(
      lastWeekSessionStatistics,
      (session) => session.goodConcentricReps,
    ),
    lastWeekAiTotalReps,
  );
  const lastWeekConcentricSlowRepsPercentage = toPercentage(
    sumSessionMetric(
      lastWeekSessionStatistics,
      (session) => session.slowConcentricReps,
    ),
    lastWeekAiTotalReps,
  );
  const lastWeekConcentricFastRepsPercentage = toPercentage(
    sumSessionMetric(
      lastWeekSessionStatistics,
      (session) => session.fastConcentricReps,
    ),
    lastWeekAiTotalReps,
  );
  const lastWeekEccentricGoodRepsPercentage = toPercentage(
    sumSessionMetric(
      lastWeekSessionStatistics,
      (session) => session.goodEccentricReps,
    ),
    lastWeekAiTotalReps,
  );
  const lastWeekEccentricSlowRepsPercentage = toPercentage(
    sumSessionMetric(
      lastWeekSessionStatistics,
      (session) => session.slowEccentricReps,
    ),
    lastWeekAiTotalReps,
  );
  const lastWeekEccentricFastRepsPercentage = toPercentage(
    sumSessionMetric(
      lastWeekSessionStatistics,
      (session) => session.fastEccentricReps,
    ),
    lastWeekAiTotalReps,
  );
  const lastMonthConcentricGoodRepsPercentage = toPercentage(
    sumSessionMetric(
      lastMonthSessionStatistics,
      (session) => session.goodConcentricReps,
    ),
    lastMonthAiTotalReps,
  );
  const lastMonthConcentricSlowRepsPercentage = toPercentage(
    sumSessionMetric(
      lastMonthSessionStatistics,
      (session) => session.slowConcentricReps,
    ),
    lastMonthAiTotalReps,
  );
  const lastMonthConcentricFastRepsPercentage = toPercentage(
    sumSessionMetric(
      lastMonthSessionStatistics,
      (session) => session.fastConcentricReps,
    ),
    lastMonthAiTotalReps,
  );

  const lastMonthEccentricGoodRepsPercentage = toPercentage(
    sumSessionMetric(
      lastMonthSessionStatistics,
      (session) => session.goodEccentricReps,
    ),
    lastMonthAiTotalReps,
  );
  const lastMonthEccentricSlowRepsPercentage = toPercentage(
    sumSessionMetric(
      lastMonthSessionStatistics,
      (session) => session.slowEccentricReps,
    ),
    lastMonthAiTotalReps,
  );
  const lastMonthEccentricFastRepsPercentage = toPercentage(
    sumSessionMetric(
      lastMonthSessionStatistics,
      (session) => session.fastEccentricReps,
    ),
    lastMonthAiTotalReps,
  );

  const allTimeConcentricGoodRepsPercentage = toPercentage(
    sumSessionMetric(
      allTimeSessionStatistics,
      (session) => session.goodConcentricReps,
    ),
    allTimeAiTotalReps,
  );
  const allTimeConcentricSlowRepsPercentage = toPercentage(
    sumSessionMetric(
      allTimeSessionStatistics,
      (session) => session.slowConcentricReps,
    ),
    allTimeAiTotalReps,
  );
  const allTimeConcentricFastRepsPercentage = toPercentage(
    sumSessionMetric(
      allTimeSessionStatistics,
      (session) => session.fastConcentricReps,
    ),
    allTimeAiTotalReps,
  );

  const allTimeEccentricGoodRepsPercentage = toPercentage(
    sumSessionMetric(
      allTimeSessionStatistics,
      (session) => session.goodEccentricReps,
    ),
    allTimeAiTotalReps,
  );
  const allTimeEccentricSlowRepsPercentage = toPercentage(
    sumSessionMetric(
      allTimeSessionStatistics,
      (session) => session.slowEccentricReps,
    ),
    allTimeAiTotalReps,
  );
  const allTimeEccentricFastRepsPercentage = toPercentage(
    sumSessionMetric(
      allTimeSessionStatistics,
      (session) => session.fastEccentricReps,
    ),
    allTimeAiTotalReps,
  );

  // Calculate missed percentages for calculateOverallStatistics
  const lastWorkoutConcentricMissedRepsPercentage = toPercentage(
    lastWorkoutAiTotalReps - lastWorkoutAiTotalPerformedReps,
    lastWorkoutAiTotalReps,
  );

  const lastWorkoutEccentricMissedRepsPercentage =
    lastWorkoutConcentricMissedRepsPercentage;

  const lastWeekConcentricMissedRepsPercentage = toPercentage(
    lastWeekAiTotalReps - lastWeekAiTotalPerformedReps,
    lastWeekAiTotalReps,
  );

  const lastWeekEccentricMissedRepsPercentage =
    lastWeekConcentricMissedRepsPercentage;

  const lastMonthConcentricMissedRepsPercentage = toPercentage(
    lastMonthAiTotalReps - lastMonthAiTotalPerformedReps,
    lastMonthAiTotalReps,
  );

  const lastMonthEccentricMissedRepsPercentage =
    lastMonthConcentricMissedRepsPercentage;

  const allTimeConcentricMissedRepsPercentage = toPercentage(
    allTimeAiTotalReps - allTimeAiTotalPerformedReps,
    allTimeAiTotalReps,
  );

  const allTimeEccentricMissedRepsPercentage =
    allTimeConcentricMissedRepsPercentage;

  return {
    lastWorkoutRepsCompletionPercentage,
    lastWeekRepsCompletionPercentage,
    lastMonthRepsCompletionPercentage,
    allTimeRepsCompletionPercentage,
    lastWorkoutConcentricGoodRepsPercentage,
    lastWorkoutConcentricSlowRepsPercentage,
    lastWorkoutConcentricFastRepsPercentage,
    lastWorkoutConcentricMissedRepsPercentage,
    lastWeekConcentricGoodRepsPercentage,
    lastWeekConcentricSlowRepsPercentage,
    lastWeekConcentricFastRepsPercentage,
    lastWeekConcentricMissedRepsPercentage,
    lastMonthConcentricGoodRepsPercentage,
    lastMonthConcentricSlowRepsPercentage,
    lastMonthConcentricFastRepsPercentage,
    lastMonthConcentricMissedRepsPercentage,
    allTimeConcentricGoodRepsPercentage,
    allTimeConcentricSlowRepsPercentage,
    allTimeConcentricFastRepsPercentage,
    allTimeConcentricMissedRepsPercentage,
    lastWorkoutEccentricGoodRepsPercentage,
    lastWorkoutEccentricSlowRepsPercentage,
    lastWorkoutEccentricFastRepsPercentage,
    lastWorkoutEccentricMissedRepsPercentage,
    lastWeekEccentricGoodRepsPercentage,
    lastWeekEccentricSlowRepsPercentage,
    lastWeekEccentricFastRepsPercentage,
    lastWeekEccentricMissedRepsPercentage,
    lastMonthEccentricGoodRepsPercentage,
    lastMonthEccentricSlowRepsPercentage,
    lastMonthEccentricFastRepsPercentage,
    lastMonthEccentricMissedRepsPercentage,
    allTimeEccentricGoodRepsPercentage,
    allTimeEccentricSlowRepsPercentage,
    allTimeEccentricFastRepsPercentage,
    allTimeEccentricMissedRepsPercentage,
  };
};

export interface SessionStatisticsBuckets {
  lastWorkout: SessionStatistic | null;
  last7Days: SessionStatistic[];
  last30Days: SessionStatistic[];
  allTime: SessionStatistic[];
}

export const buildSessionStatisticsBuckets = (
  sessionStatistics: SessionStatistic[] | null | undefined,
  options?: { now?: Date },
): SessionStatisticsBuckets => {
  const safeStats = Array.isArray(sessionStatistics) ? sessionStatistics : [];
  const referenceDate = options?.now ?? new Date();
  const sevenDaysAgo = new Date(referenceDate);
  sevenDaysAgo.setDate(referenceDate.getDate() - 7);
  const thirtyDaysAgo = new Date(referenceDate);
  thirtyDaysAgo.setDate(referenceDate.getDate() - 30);
  const sortedStats = [...safeStats].sort(
    (a, b) =>
      new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime(),
  );
  return {
    lastWorkout: sortedStats[0] ?? null,
    last7Days: sortedStats.filter(
      (stat) => new Date(stat.createdDate) >= sevenDaysAgo,
    ),
    last30Days: sortedStats.filter(
      (stat) => new Date(stat.createdDate) >= thirtyDaysAgo,
    ),
    allTime: safeStats,
  };
};
